import {
  KeyboardEvent,
  forwardRef,
  HTMLProps,
  useCallback,
  useEffect,
  useState,
  KeyboardEventHandler,
  useRef,
  Ref,
} from 'react';
import {
  getDateErrorMessage,
  getDateNotAllowedErrorMessage,
  isDateStringValid,
  parseLocaleDateString,
} from '@ardoq/date-time';
import {
  DatePickerCloseTriggerKeys,
  DateTimeInputSpecificProps,
  DateTimePickerDropdownConfig,
} from './types';
import { TextInput } from '@ardoq/forms';
import { IconName } from '@ardoq/icons';
import { useOnMount } from '@ardoq/hooks';
import { PopoverContentType } from '@ardoq/popovers';
import { isDateAllowed } from './DateTimePickerDropdown/utils';
import { Locale } from '@ardoq/locale';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

// export to DateTimePicker
export const useMultipleRefs = <T extends Element>(
  ...refs: (Ref<T> | null)[]
) => {
  const combinedRefs = (element: T | null) => {
    for (const ref of state.refs) {
      if (ref instanceof Function) {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    }
  };

  const [state] = useState({ refs, combinedRefs });

  state.refs = refs;

  return state.combinedRefs;
};

type DateInputProps = {
  value: string;
  locale: Locale;
  openDatepicker: VoidFunction;
  saveDate: (newDate: Date | null) => void;
  popoverContent?: PopoverContentType;
  config?: DateTimePickerDropdownConfig;
  changeValueOnInvalidDate?: boolean;
  closeHandlers?: Set<VoidFunction>;
  closeOnKeyHandlers?: Set<KeyboardEventHandler<HTMLInputElement>>;
  /**
   * Fires as the user edits the text, reporting whether the input differs from
   * the committed value. The value itself is still only committed on
   * blur/Enter/Tab — this just lets a consumer reflect a pending edit (e.g.
   * enabling a Save button) without waiting for the commit.
   */
  onModifiedChange?: (isModified: boolean) => void;
  i18nLocale?: I18nLocale;
} & HTMLProps<HTMLInputElement> &
  DateTimeInputSpecificProps;

const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      value,
      placeholder,
      locale,
      openDatepicker,
      saveDate,
      hideIcon,
      autoFocus,
      isReadOnly,
      errorMessage,
      config,
      changeValueOnInvalidDate,
      closeHandlers,
      closeOnKeyHandlers,
      onModifiedChange,
      i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
      ...restInputProps
    },
    forwardedInputRef
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const inputRefs = useMultipleRefs(forwardedInputRef, inputRef);

    const [dateValue, setDateValue] = useState<string>(value);
    const [dateError, setDateError] = useState<string | null>(
      errorMessage || null
    );

    const checkAndSaveDateOrSetError = useCallback(() => {
      const inputValue = inputRef.current?.value;
      if (!inputValue) {
        saveDate(null);
        return;
      }
      if (!isDateStringValid(inputValue, locale)) {
        setDateError(getDateErrorMessage(i18nLocale));
        return;
      }
      const inputDateValue = parseLocaleDateString(inputValue, locale);
      if (
        config &&
        inputDateValue &&
        !isDateAllowed(inputDateValue, config) &&
        !changeValueOnInvalidDate
      ) {
        setDateError(getDateNotAllowedErrorMessage(i18nLocale));
        return;
      }
      saveDate(inputDateValue);
    }, [
      saveDate,
      locale,
      setDateError,
      config,
      changeValueOnInvalidDate,
      i18nLocale,
    ]);

    const setDateAndClearError = useCallback(
      (newDateValue: string) => {
        setDateError(null);
        setDateValue(newDateValue);
      },
      [setDateError, setDateValue]
    );

    const resetOrSaveDate = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === DatePickerCloseTriggerKeys.ESCAPE) {
          setDateAndClearError(value);
          onModifiedChange?.(false);
        }
        if (
          e.key === DatePickerCloseTriggerKeys.ENTER ||
          e.key === DatePickerCloseTriggerKeys.TAB
        ) {
          checkAndSaveDateOrSetError();
        }
      },
      [
        value,
        setDateAndClearError,
        checkAndSaveDateOrSetError,
        onModifiedChange,
      ]
    );

    useEffect(() => {
      closeHandlers?.add(checkAndSaveDateOrSetError);
      closeOnKeyHandlers?.add(resetOrSaveDate);

      return () => {
        closeHandlers?.delete(checkAndSaveDateOrSetError);
        closeOnKeyHandlers?.delete(resetOrSaveDate);
      };
    }, [
      closeHandlers,
      closeOnKeyHandlers,
      checkAndSaveDateOrSetError,
      resetOrSaveDate,
    ]);

    useEffect(() => {
      setDateAndClearError(value);
    }, [value, setDateAndClearError]);

    useOnMount(() => {
      if (autoFocus) openDatepicker();
    });

    const resolvedPlaceholder =
      placeholder ??
      t(i18nLocale, 'dateTimePicker:selectDatePlaceholder', 'Select date...');

    return (
      <TextInput
        id={restInputProps.inputId}
        leftIconName={hideIcon || isReadOnly ? undefined : IconName.CALENDAR}
        ref={inputRefs as any}
        value={dateValue}
        placeholder={resolvedPlaceholder}
        errorMessage={errorMessage ?? (dateError !== null ? dateError : '')}
        onClick={openDatepicker}
        onChange={e => {
          setDateAndClearError(e.target.value);
          onModifiedChange?.(e.target.value !== value);
        }}
        spellCheck={false}
        autoFocus={autoFocus}
        isReadOnly={isReadOnly}
        {...restInputProps}
      />
    );
  }
);

export { DateInput };
